import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';
import DashboardLayout from '../../components/layouts/DashboardLayout';
import { getVoucherByid, updateVoucher } from '../../components/actions/voucher';
import { uploadImage } from '../../components/actions/campaign';
import Loader from '../../components/utils/Loader';
import Alert from './Alert';

export default function EditVoucherPage() {
  const navigate = useNavigate();
  const { code } = useParams();
  const imageInputRefBanner = useRef();
  const merchantCode = sessionStorage.getItem('code');

  const [loading, setLoading] = useState(false);
  const [bannerSize, setBannerSize] = useState(false);
  const [imageError, setImageError] = useState(null);
  const [selectedBanner, setSelectedBanner] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [showImageSuccessModal, setShowImageSuccessModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [startDateError, setStartDateError] = useState(false);
  const [endDateError, setEndDateError] = useState(false);
  const [voucherVisibleError, setVoucherVisibleError] = useState(false);
  const [alert, setAlert] = useState({ message: '', type: '' });

  const [state, setState] = useState({
    id: 0,
    merchantCode: merchantCode || '',
    code: code,
    name: '',
    startDate: '',
    endDate: '',
    visibilityDate: '',
    voucherType: '',
    voucherMode: 'I',
    redeemUrl: '',
    termsAndConditions: '',
    description: '',
    image: 'string',
    remarks1: '',
    remarks2: '',
    status: 0, // Use 0/1 consistently
    voucherCount: 0,
    voucherValue: 0,
    voucherExpiryDate:""
  });

  const voucherValueOptions = [
    { value: 'No Value', label: 'No Value' },
    { value: 'Fix Value', label: 'Fix Value' },
  ];

  // Fetch voucher data on mount
  useEffect(() => {
    if (code) {
      setLoading(true);
      getVoucherByid(merchantCode, code)
        .then((res) => {
          const voucherData = Array.isArray(res?.data) ? res.data[0] : res.data;

          // Normalize voucherMode
          const mode = ['1', 1, 'G'].includes(voucherData.voucherMode) ? 'G' : 'I';

          // Normalize status to 0 or 1
          const normalizedStatus = [true, 'true', 1, '1'].includes(voucherData.status) ? 1 : 0;

          setState({
            id: voucherData.id || 0,
            merchantCode: voucherData.merchantCode || merchantCode,
            code: voucherData.code || code,
            name: voucherData.name || '',
            startDate: voucherData.startDate || '',
            endDate: voucherData.endDate || '',
            visibilityDate: voucherData.visibilityDate || '',
            voucherType: voucherData.voucherType || '',
            voucherMode: voucherData?.voucherMode,
            redeemUrl: voucherData.redeemUrl || '',
            termsAndConditions: voucherData.termsAndConditions || '',
            description: voucherData.description || '',
            voucherExpiryDate:voucherData?.voucherExpiryDate || "",
            image: voucherData.image || 'string',
            remarks1: voucherData.remarks1 || '',
            remarks2: voucherData.remarks2 || '',
            status: normalizedStatus, // 0 or 1
            voucherCount: voucherData.voucherCount || 0,
            voucherValue: voucherData.voucherValue || 0,
          });

          const fetchedImageUrl = voucherData.image !== 'string' ? voucherData.image : '';
          setImageUrl(fetchedImageUrl);
          setImagePreview(fetchedImageUrl);
          setLoading(false);
        })
        .catch((err) => {
          console.error('Error fetching voucher:', err);
          setAlert({ message: 'Failed to fetch voucher data', type: 'error' });
          setLoading(false);
        });
    }
  }, [code, merchantCode]);

  // Validate end date > start date
  useEffect(() => {
    if (state.startDate && state.endDate) {
      const diff = moment(state.endDate).diff(moment(state.startDate), 'days');
      setEndDateError(diff < 0);
    } else {
      setEndDateError(false);
    }
  }, [state.startDate, state.endDate]);

  // Validate visibility date between start and end
  useEffect(() => {
    if (state.startDate && state.endDate && state.visibilityDate) {
      const visStartDiff = moment(state.visibilityDate).diff(moment(state.startDate), 'days');
      const endVisDiff = moment(state.endDate).diff(moment(state.visibilityDate), 'days');
      setVoucherVisibleError(visStartDiff < 0 || endVisDiff < 0);
    } else {
      setVoucherVisibleError(false);
    }
  }, [state.startDate, state.endDate, state.visibilityDate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'voucherCount' || name === 'voucherValue') {
      const numericValue = value.replace(/[^0-9]/g, '');
      setState({ ...state, [name]: Number(numericValue) || 0 });
    } else {
      setState({ ...state, [name]: value });
    }
  };

  const handleToggle = (e) => {
    const { name, checked } = e.target;
    if (name === 'voucherMode') {
      setState({ ...state, [name]: checked ? 'G' : 'I' });
    } else {
      setState({ ...state, [name]: checked ? 1 : 0 });
    }
  };

  const handleFileBanner = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setImageError('Please select a valid image file');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setImageError('Image size should be less than 5MB');
        setBannerSize(true);
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        setSelectedBanner(file);
        setImagePreview(e.target.result);
        setImageUrl('');
        setBannerSize(false);
        setImageError(null);
        setState({ ...state, image: file });
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setSelectedBanner(null);
    setImagePreview('');
    setImageUrl('');
    setImageError(null);
    setBannerSize(false);
    setState({ ...state, image: 'string' });
    if (imageInputRefBanner.current) {
      imageInputRefBanner.current.value = '';
    }
  };

  const handleImageSubmit = async () => {
    if (!selectedBanner) {
      setImageError('No image selected for upload');
      return;
    }

    setLoading(true);
    setImageError(null);
    try {
      const response = await uploadImage(merchantCode, selectedBanner);
      if (response && response.url) {
        setImageUrl(response.url);
        setImagePreview(response.url);
        setState((prev) => ({ ...prev, image: response.url }));
        setShowImageSuccessModal(true);
      } else {
        setImageError('Failed to upload image. Please try again.');
      }
    } catch (err) {
      setImageError('Error uploading image. Please try again.');
      console.error('Upload error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleImageSuccessClose = () => {
    setShowImageSuccessModal(false);
  };

  const handleReset = () => {
    setState({
      id: 0,
      merchantCode: merchantCode || '',
      code: code,
      name: '',
      startDate: '',
      endDate: '',
      visibilityDate: '',
      voucherType: '',
      voucherMode: 'I',
      redeemUrl: '',
      termsAndConditions: '',
      description: '',
      image: 'string',
      remarks1: '',
      remarks2: '',
      status: 0,
      voucherCount: 0,
      voucherValue: 0,
      voucherExpiryDate:""
    });
    setSelectedBanner(null);
    setImagePreview('');
    setImageUrl('');
    setBannerSize(false);
    setImageError(null);
    if (imageInputRefBanner.current) {
      imageInputRefBanner.current.value = '';
    }
  };

  const handleSuccessClose = () => {
    setShowSuccessModal(false);
    navigate('/vouchers');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!state.name || !state.startDate || !state.endDate || !state.visibilityDate ||!state.voucherExpiryDate || !state.voucherType) {
      setAlert({ message: 'Please fill in all required fields', type: 'error' });
      return;
    }
    if (endDateError || voucherVisibleError) {
      setAlert({ message: 'Please correct the date errors', type: 'error' });
      return;
    }
    if (state.voucherType === 'Fix Value' && !state.voucherValue) {
      setAlert({ message: 'Please enter a voucher value', type: 'error' });
      return;
    }
    if (state.voucherMode === 'G' && !state.voucherCount) {
      setAlert({ message: 'Please enter the number of vouchers to generate', type: 'error' });
      return;
    }

    setLoading(true);
    try {
           const responses = await uploadImage(merchantCode, selectedBanner);
      const updatedState = {
        ...state,
       image: responses?.url || "",
        status: state.status, // Already 0 or 1
      };
      const response = await updateVoucher(updatedState);

      if (response?.message === 'success') {
        setAlert({ message: 'Voucher Template Updated Successfully', type: 'success' });
        setShowSuccessModal(true);
      } else {
        throw new Error(response?.message || 'Failed to update voucher');
      }
    } catch (err) {
      setAlert({ message: err.message || 'An error occurred', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      {loading && <Loader />}
      {alert.message && (
        <Alert
          message={alert.message}
          type={alert.type}
          onClose={() => setAlert({ message: '', type: '' })}
        />
      )}
      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex items-center gap-4 mb-6">
          <Link
            to="/vouchers"
            className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-white rounded-xl transition-all duration-200 cursor-pointer shadow-sm border border-gray-200"
          >
            <i className="ri-arrow-left-line text-lg"></i>
          </Link>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Edit Voucher Template</h2>
            <p className="text-gray-600 mt-1">Update an existing voucher</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Voucher Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={state.name}
                onChange={handleChange}
                placeholder="Enter voucher name"
                maxLength="50"
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:outline-none bg-gray-50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Voucher Validity Start Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="startDate"
                value={state.startDate ? moment(state.startDate).format('YYYY-MM-DD') : ''}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:outline-none bg-gray-50"
              />
              {startDateError && (
                <span className="text-red-500 text-sm">Start date should not be less than today</span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Voucher Validity End Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="endDate"
                value={state.endDate ? moment(state.endDate).format('YYYY-MM-DD') : ''}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:outline-none bg-gray-50"
              />
              {endDateError && (
                <span className="text-red-500 text-sm">End date should be greater than start date</span>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Voucher Visible From Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="visibilityDate"
                value={state.visibilityDate ? moment(state.visibilityDate).format('YYYY-MM-DD') : ''}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:outline-none bg-gray-50"
              />
              {voucherVisibleError && (
                <span className="text-red-500 text-sm">
                  Voucher visibility date should be between start and end date
                </span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Voucher Value Type <span className="text-red-500">*</span>
              </label>
              <select
                name="voucherType"
                value={state.voucherType}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:outline-none bg-gray-50 appearance-none"
              >
                <option value="">Select Type</option>
                {voucherValueOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            {state.voucherType === 'Fix Value' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Voucher Value <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="voucherValue"
                  value={state.voucherValue}
                  onChange={handleChange}
                  placeholder="Enter voucher value"
                  maxLength="7"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:outline-none bg-gray-50"
                />
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Voucher Mode</label>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  name="voucherMode"
                  checked={state.voucherMode === 'G'}
                  onChange={handleToggle}
                  disabled
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-green-500 peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
                <span className="ml-3 text-sm font-medium text-gray-900">
                  {state.voucherMode === 'G' ? 'Generated' : 'Imported'}
                </span>
              </label>
            </div>
            {state.voucherMode === 'G' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Generate No of Vouchers <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="voucherCount"
                  value={state.voucherCount}
                  onChange={handleChange}
                  placeholder="Enter number of vouchers"
                  maxLength="7"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:outline-none bg-gray-50"
                />
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Upload Image</label>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {imagePreview ? (
                  <div className="relative">
                    <img
                      src={imagePreview}
                      alt="Voucher preview"
                      className="w-full h-32 object-cover object-top rounded-xl border border-gray-200"
                    />
                    <div className="absolute top-2 right-2 flex gap-2">
                      <button
                        type="button"
                        onClick={handleImageSubmit}
                        disabled={loading || imageUrl === imagePreview}
                        className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center hover:bg-blue-600 transition-colors cursor-pointer disabled:opacity-50"
                      >
                        <i className="ri-upload-line text-xs"></i>
                      </button>
                      <button
                        type="button"
                        onClick={removeImage}
                        className="w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors cursor-pointer"
                      >
                        <i className="ri-close-line text-xs"></i>
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-gray-300 rounded-xl p-4 text-center hover:border-blue-400 transition-colors">
                    <i className="ri-image-line text-2xl text-gray-400 mb-2"></i>
                    <p className="text-gray-600 text-sm mb-1">Upload image</p>
                    <p className="text-xs text-gray-500 mb-3">PNG, JPG up to 5MB</p>
                    <label className="inline-flex items-center gap-1 px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer text-sm">
                      <i className="ri-upload-line text-sm"></i>
                      Choose
                      <input
                        type="file"
                        ref={imageInputRefBanner}
                        accept="image/*"
                        onChange={handleFileBanner}
                        className="hidden"
                      />
                    </label>
                  </div>
                )}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  name="status"
                  checked={state.status === 1}
                  onChange={handleToggle}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-green-500 peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
                <span className="ml-3 text-sm font-medium text-gray-900">
                  {state.status === 1 ? 'Active' : 'Inactive'}
                </span>
              </label>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Redeem URL</label>
              <input
                type="text"
                name="redeemUrl"
                value={state.redeemUrl}
                onChange={handleChange}
                maxLength="500"
                placeholder="Enter redeem URL"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:outline-none bg-gray-50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Terms and Conditions</label>
              <input
                type="text"
                name="termsAndConditions"
                value={state.termsAndConditions}
                onChange={handleChange}
                maxLength="500"
                placeholder="Enter terms and conditions"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:outline-none bg-gray-50"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Remark</label>
              <input
                type="text"
                name="remarks2"
                value={state.remarks2}
                onChange={handleChange}
                maxLength="500"
                placeholder="Enter remark"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:outline-none bg-gray-50"
              />
            </div>
                  <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Voucher Expiry Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="voucherExpiryDate"
                 value={state.voucherExpiryDate ? moment(state.voucherExpiryDate).format('YYYY-MM-DD') : ''}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:outline-none bg-gray-50"
              />
            
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
            <textarea
              name="description"
              rows={4}
              value={state.description}
              onChange={handleChange}
              maxLength="500"
              placeholder="Enter voucher description"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:outline-none resize-none bg-gray-50"
            ></textarea>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={handleReset}
              className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-colors"
            >
              Reset
            </button>
            <button
              type="submit"
              className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
            >
              <i className="ri-edit-line text-lg"></i>
              Update Voucher
            </button>
          </div>
        </form>
      </div>

      {/* Image Success Modal */}
      {showImageSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-check-line text-3xl text-green-600"></i>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Image Uploaded Successfully</h2>
              <p className="text-gray-600 mb-6 break-words">Image URL: {imageUrl}</p>
              <button
                onClick={handleImageSuccessClose}
                className="px-6 py-3 bg-blue-500 text-white rounded-xl font-semibold hover:bg-blue-600 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-check-line text-3xl text-green-600"></i>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Voucher Updated Successfully</h2>
              <p className="text-gray-600 mb-6">
                Your voucher "{state.name}" has been updated successfully.
              </p>
              <button
                onClick={handleSuccessClose}
                className="flex-1 px-6 py-3 bg-blue-500 text-white rounded-xl font-semibold hover:bg-blue-600 transition-colors"
              >
                View Vouchers
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}